import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/authContext";

export interface Benchmarks {
  laborPct: number;
  foodPct: number;
  rentPct: number;
}

export const DEFAULT_BENCHMARKS: Benchmarks = {
  laborPct: 30,
  foodPct: 32,
  rentPct: 10,
};

/** Loads the signed-in user's benchmark targets, falling back to industry
 *  defaults. `save` upserts the new values and updates local state. */
export function useBenchmarks() {
  const { user } = useAuth();
  const [benchmarks, setBenchmarks] = useState<Benchmarks>(DEFAULT_BENCHMARKS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setBenchmarks(DEFAULT_BENCHMARKS);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await (supabase as any)
        .from("user_benchmarks")
        .select("labor_pct_target, food_pct_target, rent_pct_target")
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled) return;
      if (data) {
        setBenchmarks({
          laborPct: Number(data.labor_pct_target),
          foodPct: Number(data.food_pct_target),
          rentPct: Number(data.rent_pct_target),
        });
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user]);

  const save = useCallback(async (next: Benchmarks) => {
    if (!user) return { error: "Not signed in" as const };
    const { error } = await (supabase as any)
      .from("user_benchmarks")
      .upsert(
        {
          user_id: user.id,
          labor_pct_target: next.laborPct,
          food_pct_target: next.foodPct,
          rent_pct_target: next.rentPct,
        },
        { onConflict: "user_id" },
      );
    if (error) return { error: error.message };
    setBenchmarks(next);
    return { error: null };
  }, [user]);

  return { benchmarks, loading, save };
}